import type { TypedBroadcastChannel } from '../../util/browser/multitab';
import type { LocalDb } from '../localDb';
import type { MethodArgs, MethodResponse, Methods } from '../methods';
import type {
  ApiError,
  ApiInitialArgs,
  ApiOnProgress,
  OnApiUpdate,
} from '../types';
import type { OriginPayload, WorkerMessageEvent } from './types';

import { DEBUG, IGNORE_UNHANDLED_ERRORS } from '../../config';
import { IS_TAURI } from '../../util/browser/globalEnvironment';
import {
  APP_WAKE_EVENT,
  IS_SAFARI,
} from '../../util/browser/windowEnvironment';
import { logDebugMessage } from '../../util/debugConsole';
import Deferred from '../../util/Deferred';
import {
  getCurrentTabId,
  reestablishMasterToSelf,
  subscribeToMasterChange,
} from '../../util/establishMultitabRole';
import generateUniqueId from '../../util/generateUniqueId';
import {
  ACCOUNT_SLOT,
  DATA_BROADCAST_CHANNEL_NAME,
} from '../../util/multiaccount';
import {
  clearStaleBuildRecovery,
  recoverFromStaleBuild,
} from '../../util/recoverStaleBuild';
import { pause, throttleWithTickEnd } from '../../util/schedulers';

type RequestState = {
  messageId: string;
  resolve: AnyToVoidFunction;
  reject: AnyToVoidFunction;
  callback?: AnyToVoidFunction;
  // Set only for requests routed to the master tab (see `makeRequestToMaster`).
  masterRequestName?: string;
  timeoutId?: number;
  DEBUG_payload?: any;
};

type EnsurePromise<T> = Promise<Awaited<T>>;

const HEALTH_CHECK_TIMEOUT = 150;
const HEALTH_CHECK_MIN_DELAY = 5 * 1000; // 5 sec
// A worker whose script 404s (deploy purged the old hashed chunk) never answers `initApi`, so cap the
// wait — past this we treat the worker as unloadable and recover instead of queuing requests forever.
const INIT_TIMEOUT = 20 * 1000; // 20 sec
const NO_QUEUE_BEFORE_INIT = new Set(['destroy']);

let worker: Worker | undefined;

const requestStates = new Map<string, RequestState>();
const requestStatesByCallback = new Map<AnyToVoidFunction, RequestState>();

let pendingPayloads: OriginPayload[] = [];

const savedLocalDb: LocalDb = {
  chats: {},
  users: {},
  documents: {},
  stickerSets: {},
  photos: {},
  webDocuments: {},
  commonBoxState: {},
  channelPtsById: {},
};

let isMasterTab = true;
subscribeToMasterChange((isMasterTabNew) => {
  isMasterTab = isMasterTabNew;
});

const channel = new BroadcastChannel(
  DATA_BROADCAST_CHANNEL_NAME,
) as TypedBroadcastChannel;

const postMessagesOnTickEnd = throttleWithTickEnd(() => {
  const payloads = pendingPayloads;
  pendingPayloads = [];
  worker?.postMessage({ payloads });
});

function postMessageOnTickEnd(payload: OriginPayload) {
  pendingPayloads.push(payload);
  postMessagesOnTickEnd();
}

export function initApiOnMasterTab(initialArgs: ApiInitialArgs) {
  channel.postMessage({
    type: 'initApi',
    token: getCurrentTabId(),
    initialArgs,
  });
}

let updateCallback: OnApiUpdate;

let localApiRequestsQueue: {
  fnName: any;
  args: any;
  deferred: Deferred<any>;
}[] = [];
let apiRequestsQueue: { fnName: any; args: any; deferred: Deferred<any> }[] =
  [];
let isInited = false;

export function initApi(onUpdate: OnApiUpdate, initialArgs: ApiInitialArgs) {
  updateCallback = onUpdate;
  setupConnectionWake();

  if (!isMasterTab) {
    initApiOnMasterTab(initialArgs);
    return Promise.resolve();
  }

  if (!worker) {
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log('>>> START LOAD WORKER');
    }

    const params = new URLSearchParams();
    if (ACCOUNT_SLOT) {
      params.set('account', String(ACCOUNT_SLOT));
    }

    worker = new Worker(new URL('./worker.ts', import.meta.url), {
      name: params.toString(),
    });
    // A failed worker-script load (e.g. a 404 on the old hashed chunk after a deploy) fires `error`
    // and never answers `initApi`; recover instead of hanging silently.
    worker.addEventListener('error', handleWorkerLoadError);
    subscribeToWorker(onUpdate);

    if (IS_SAFARI || (initialArgs.platform === 'macOS' && IS_TAURI)) {
      setupHealthCheck();
    }
  }

  let isInitSettled = false;

  return Promise.race([
    makeRequest({
      type: 'initApi',
      args: [initialArgs, savedLocalDb],
    }),
    pause(INIT_TIMEOUT).then(() =>
      isInitSettled ? undefined : Promise.reject(new Error('INIT_TIMEOUT')),
    ),
  ])
    .then(() => {
      isInitSettled = true;
      isInited = true;
      // The worker booted, so this build's chunks are being served — allow future recoveries again.
      clearStaleBuildRecovery();

      apiRequestsQueue.forEach((request) => {
        callApi(request.fnName, ...request.args)
          .then(request.deferred.resolve)
          .catch(request.deferred.reject);
      });
      apiRequestsQueue = [];

      localApiRequestsQueue.forEach((request) => {
        callApiLocal(request.fnName, ...request.args)
          .then(request.deferred.resolve)
          .catch(request.deferred.reject);
      });
      localApiRequestsQueue = [];
    })
    .catch((err) => {
      isInitSettled = true;
      // eslint-disable-next-line no-console
      console.error('[API] Failed to initialize worker', err);
      void recoverFromStaleBuild();
    });
}

function handleWorkerLoadError(e: ErrorEvent) {
  // The `error` event also fires for an uncaught exception INSIDE a running worker, not only for a
  // script that failed to load. Once `initApi` has answered, this build's chunk is being served, so a
  // later error is a runtime bug — reloading on it (and wiping the caches first) re-ran the same code
  // into the same error every few seconds, leaving the user with a page that never stopped reloading
  if (isInited) {
    // eslint-disable-next-line no-console
    console.error('[API] Uncaught error in worker', e.message);
    return;
  }

  // eslint-disable-next-line no-console
  console.error('[API] Worker failed to load', e.message);
  void recoverFromStaleBuild();
}

export function updateLocalDb(name: keyof LocalDb, prop: string, value: any) {
  savedLocalDb[name][prop] = value;
}

export function updateFullLocalDb(initial: LocalDb) {
  Object.assign(savedLocalDb, initial);
}

export function callApiOnMasterTab(payload: any) {
  channel.postMessage({
    type: 'callApi',
    token: getCurrentTabId(),
    ...payload,
  });
}

export function setShouldEnableDebugLog(value: boolean) {
  return makeRequest({
    type: 'toggleDebugMode',
    isEnabled: value,
  });
}

/*
 * Call a worker method on this tab's worker, without transferring to master tab
 * Mostly needed to disconnect worker when re-electing master
 */
export function callApiLocal<T extends keyof Methods>(
  fnName: T,
  ...args: MethodArgs<T>
): EnsurePromise<MethodResponse<T>>;
export function callApiLocal(fnName: string, ...args: any[]): Promise<any>;
export function callApiLocal(fnName: any, ...args: any[]): any {
  if (!isInited) {
    if (NO_QUEUE_BEFORE_INIT.has(fnName)) {
      return Promise.resolve(undefined);
    }

    const deferred = new Deferred();
    localApiRequestsQueue.push({ fnName, args, deferred });

    return deferred.promise;
  }

  const promise = makeRequest({
    type: 'callMethod',
    name: fnName,
    args,
  });

  return promise;
}

export function callApi<T extends keyof Methods>(
  fnName: T,
  ...args: MethodArgs<T>
): EnsurePromise<MethodResponse<T>>;
export function callApi(fnName: string, ...args: any[]): Promise<any>;
export function callApi(fnName: any, ...args: any[]): any {
  if (!isInited && isMasterTab) {
    if (NO_QUEUE_BEFORE_INIT.has(fnName)) {
      return Promise.resolve(undefined);
    }

    const deferred = new Deferred();
    apiRequestsQueue.push({ fnName, args, deferred });

    return deferred.promise;
  }

  const promise = isMasterTab
    ? makeRequest({
      type: 'callMethod',
      name: fnName,
      args,
    })
    : makeRequestToMaster({
      name: fnName,
      args,
    });

  return promise;
}

export function cancelApiProgress(progressCallback: ApiOnProgress) {
  progressCallback.isCanceled = true;

  const { messageId } = requestStatesByCallback.get(progressCallback) || {};
  if (!messageId) {
    return;
  }

  if (isMasterTab) {
    cancelApiProgressMaster(messageId);
  } else {
    channel.postMessage({
      type: 'cancelApiProgress',
      token: getCurrentTabId(),
      messageId,
    });
  }
}

export function cancelApiProgressMaster(messageId: string) {
  postMessageOnTickEnd({
    type: 'cancelProgress',
    messageId,
  });
}

function subscribeToWorker(onUpdate: OnApiUpdate) {
  worker?.addEventListener('message', ({ data }: WorkerMessageEvent) => {
    data?.payloads.forEach((payload) => {
      if (payload.type === 'updates') {
        let DEBUG_startAt: number | undefined;
        if (DEBUG) {
          DEBUG_startAt = performance.now();
        }

        payload.updates.forEach(onUpdate);

        if (DEBUG) {
          const duration = performance.now() - DEBUG_startAt!;
          if (duration > 5) {
            // eslint-disable-next-line no-console
            console.warn(
              `[API] Slow updates processing: ${payload.updates.length} updates in ${duration} ms`,
            );
          }
        }
      } else if (payload.type === 'methodResponse') {
        handleMethodResponse(payload);
      } else if (payload.type === 'methodCallback') {
        handleMethodCallback(payload);
      } else if (payload.type === 'unhandledError') {
        const message = payload.error?.message;
        if (message && IGNORE_UNHANDLED_ERRORS.has(message)) return;
        throw new Error(message);
      } else if (payload.type === 'sendBeacon') {
        navigator.sendBeacon(payload.url, payload.data);
      } else if (payload.type === 'debugLog') {
        logDebugMessage(payload.level, ...payload.args);
      }
    });
  });
}

export function handleMethodResponse(data: {
  messageId: string;
  response?: any;
  error?: Pick<ApiError, 'message' | 'code' | 'hasErrorKey'>;
}) {
  const requestState = requestStates.get(data.messageId);
  if (requestState) {
    if (data.error) {
      requestState.reject(data.error);
    } else {
      requestState.resolve(data.response);
    }
  }
}

export function handleMethodCallback(data: {
  messageId: string;
  callbackArgs: any[];
}) {
  const requestState = requestStates.get(data.messageId);
  if (!requestState) return;
  restartMasterRequestTimeout(requestState);
  requestState.callback?.(...data.callbackArgs);
}

function makeRequestToMaster(message: {
  messageId?: string;
  name: string;
  args: any[];
  withCallback?: boolean;
}) {
  const messageId = generateUniqueId();
  const payload = {
    messageId,
    ...message,
  };

  const requestState = { messageId } as RequestState;

  // Re-wrap type because of `postMessage`
  const promise = new Promise<any>((resolve, reject) => {
    Object.assign(requestState, { resolve, reject });
  });

  if (
    'args' in payload &&
    'name' in payload &&
    typeof payload.args[1] === 'function'
  ) {
    payload.withCallback = true;

    const callback = payload.args.pop() as AnyToVoidFunction;
    requestState.callback = callback;
    requestStatesByCallback.set(callback, requestState);
  }

  requestStates.set(messageId, requestState);
  startMasterRequestTimeout(requestState, message.name);

  promise
    .catch(() => undefined)
    .finally(() => {
      requestStates.delete(messageId);
      clearMasterRequestTimeout(requestState);

      if (requestState.callback) {
        requestStatesByCallback.delete(requestState.callback);
      }
    });

  callApiOnMasterTab(payload);

  return promise;
}

// The master tab answers over a `BroadcastChannel`, and nothing guarantees it ever will: if it is
// frozen or discarded by the browser (a background tab), or wedged on a long task, the request just
// sits there. Without a deadline every `callApi` from this tab hangs forever — the chat list keeps
// rendering from cache while the message list spins, the header stays on "updating" and sends never
// resolve. Reloading does not help, because a reloaded tab stays non-master and asks the same dead
// master again. So: give up after this long without a single byte back, take over the master role,
// and resolve the call as failed so the UI shows a real outcome instead of an endless spinner.
// Kept above the worker-side budgets (30s per REST request, 120s per upload — the latter keeps the
// timer alive through its progress callbacks) so a merely slow backend never looks like a dead master.
const MASTER_REQUEST_TIMEOUT = 45 * 1000; // 45 sec
// Progress-capable requests (uploads) can legitimately go silent between progress callbacks on a slow
// link while the master is perfectly alive — its worker allows up to 120s per upload. Deadline them
// above that budget so a stalled-but-alive upload is never mistaken for a dead master (which would
// fail the call and force a spurious takeover); a genuinely dead master still gives up, just later.
const MASTER_REQUEST_TIMEOUT_WITH_PROGRESS = 150 * 1000; // 150 sec

function startMasterRequestTimeout(requestState: RequestState, name: string) {
  requestState.masterRequestName = name;
  const timeout = requestState.callback
    ? MASTER_REQUEST_TIMEOUT_WITH_PROGRESS
    : MASTER_REQUEST_TIMEOUT;
  requestState.timeoutId = self.setTimeout(() => {
    requestState.timeoutId = undefined;
    // eslint-disable-next-line no-console
    console.warn(
      `[API] Master tab did not answer "${name}" in ${timeout}ms, taking over`,
    );
    reestablishMasterToSelf();
    requestState.resolve(undefined);
  }, timeout);
}

// Progress callbacks prove the master is alive, so treat the deadline as inactivity, not duration —
// otherwise a large upload would trip it mid-flight.
function restartMasterRequestTimeout(requestState: RequestState) {
  if (!requestState.masterRequestName || !requestState.timeoutId) return;
  clearMasterRequestTimeout(requestState);
  startMasterRequestTimeout(requestState, requestState.masterRequestName);
}

function clearMasterRequestTimeout(requestState: RequestState) {
  if (!requestState.timeoutId) return;
  clearTimeout(requestState.timeoutId);
  requestState.timeoutId = undefined;
}

function makeRequest(message: OriginPayload) {
  const messageId = generateUniqueId();
  const payload: OriginPayload = {
    messageId,
    ...message,
  };

  const requestState = { messageId } as RequestState;

  // Re-wrap type because of `postMessage`
  const promise = new Promise<any>((resolve, reject) => {
    Object.assign(requestState, { resolve, reject });
  });

  if (
    'args' in payload &&
    'name' in payload &&
    typeof payload.args[1] === 'function'
  ) {
    payload.withCallback = true;

    const callback = payload.args.pop() as AnyToVoidFunction;
    requestState.callback = callback;
    requestStatesByCallback.set(callback, requestState);
  }

  requestState.DEBUG_payload = payload;

  requestStates.set(messageId, requestState);

  promise
    .catch(() => undefined)
    .finally(() => {
      requestStates.delete(messageId);

      if (requestState.callback) {
        requestStatesByCallback.delete(requestState.callback);
      }
    });

  postMessageOnTickEnd(payload);

  return promise;
}

const startedAt = Date.now();

let isConnectionWakeSetup = false;
let isHealthCheckSetup = false;
let hiddenAt: number | undefined;

// A backgrounded tab can lose its realtime connection without recovering: the OS may silently drop
// the WebSocket (sleep, network change, server idle-kill) leaving a half-open "zombie" socket, and
// background timer throttling can freeze the reconnect backoff. Neither self-heals on return, so the
// chat sits on stale data until a manual reload (the reported "must F5" symptom). On every return to
// the foreground, nudge the master tab's worker to verify the realtime socket and reconnect + resync
// if needed. Runs on every platform (unlike `setupHealthCheck`, which is Safari/Tauri-only) and is
// idempotent so repeated `initApi` calls (e.g. `requestReconnectApi`) don't stack listeners.
function setupConnectionWake() {
  if (isConnectionWakeSetup || typeof document === 'undefined') return;
  isConnectionWakeSetup = true;

  function ensureConnectionAfterWake() {
    void callApi('ensureUpdatesConnection', 0);
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
      return;
    }

    const hiddenMs = hiddenAt !== undefined ? Date.now() - hiddenAt : 0;
    hiddenAt = undefined;
    // Routes to the master tab's worker, which owns the realtime socket.
    void callApi('ensureUpdatesConnection', hiddenMs);
  });
  window.addEventListener(APP_WAKE_EVENT, ensureConnectionAfterWake);
}

// Workaround for iOS sometimes stops interacting with worker
// Idempotent: each worker replacement re-enters the creation branch and would stack another listener
function setupHealthCheck() {
  if (isHealthCheckSetup) return;
  isHealthCheckSetup = true;

  window.addEventListener('focus', () => {
    void ensureWorkerPing();
    // Sometimes a single check is not enough
    setTimeout(() => ensureWorkerPing(), 1000);
  });
}

async function ensureWorkerPing() {
  let isResolved = false;

  try {
    await Promise.race([
      makeRequest({ type: 'ping' }),
      pause(HEALTH_CHECK_TIMEOUT).then(() =>
        isResolved
          ? undefined
          : Promise.reject(new Error('HEALTH_CHECK_TIMEOUT')),
      ),
    ]);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);

    if (Date.now() - startedAt >= HEALTH_CHECK_MIN_DELAY) {
      worker?.terminate();
      worker = undefined;
      updateCallback({ '@type': 'requestReconnectApi' });
    }
  } finally {
    isResolved = true;
  }
}
